import React, { useState, useEffect } from "react";
import { Plus, Edit2, Trash2, Check, X, Calendar, ChevronLeft, ChevronRight, LayoutList, ClipboardCheck, Printer, Star } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useWeeklyStatus } from "../hooks/useWeeklyStatus";
import { useCompanyLogos } from "../hooks/useCompanyLogos";
import { db } from "../firebase/config";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { openDocumentPrintWindow } from "../utils/pdfExportUtils";

export default function WeeklyStatus() {
  const { currentUser } = useAuth();
  const userId = currentUser?.uid || currentUser?.email || "anonymous";

  // --- Week Management Helpers ---
  const getMondayOfDate = (d) => {
    const date = new Date(d);
    const day = date.getDay();
    const diff = date.getDate() - day + (day === 0 ? -6 : 1); // adjust when day is sunday (0)
    const monday = new Date(date.setDate(diff));
    return monday.toISOString().split("T")[0];
  };

  const getWeekDisplayRange = (mondayStr) => {
    const monday = new Date(mondayStr + "T00:00:00");
    const sunday = new Date(monday.getTime() + 6 * 24 * 60 * 60 * 1000);
    
    const options = { month: "short", day: "numeric", year: "numeric" };
    return `${monday.toLocaleDateString(undefined, options)} – ${sunday.toLocaleDateString(undefined, options)}`;
  };

  const { companyLogos } = useCompanyLogos();
  const [selectedWeek, setSelectedWeek] = useState(() => getMondayOfDate(new Date()));
  const {
    headers,
    items,
    loading,
    error,
    addHeader,
    updateHeader,
    deleteHeader,
    addWeeklyItem,
    updateWeeklyItem,
    deleteWeeklyItem,
    toggleStar
  } = useWeeklyStatus(userId, selectedWeek);

  // --- User Profile Name Loader ---
  const [submitterName, setSubmitterName] = useState("");

  useEffect(() => {
    if (!currentUser || !currentUser.email) return;
    const q = query(collection(db, "users"), where("email", "==", currentUser.email));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      if (!querySnapshot.empty) {
        const data = querySnapshot.docs[0].data();
        if (data.name) {
          setSubmitterName(data.name);
        } else {
          setSubmitterName(currentUser.email.split("@")[0]);
        }
      } else {
        setSubmitterName(currentUser.email.split("@")[0]);
      }
    });
    return () => unsubscribe();
  }, [currentUser]);

  // --- UI Tab State ---
  const [activeTab, setActiveTab] = useState("status"); // "status" | "headers"

  // --- Export PDF Accomplishments Report ---
  const handleExportPDF = () => {
    const longarmLogo = companyLogos?.find(l => l.name?.toLowerCase().includes("longarm")) || companyLogos?.find(l => l.isActive);
    const activeLogoUrl = longarmLogo?.url || "https://jimini-logistics.web.app/favicon.png";
    const primaryColor = "#17365D";
    const companyName = longarmLogo?.name || "Longarm Technologies, LLC";

    // Gather content
    let contentHtml = "";
    headers.forEach(header => {
      const headerItems = items.filter(item => item.headerId === header.id);
      if (headerItems.length > 0) {
        contentHtml += `
          <div class="section-container">
            <h2 class="section-header">${header.name}</h2>
            <table class="status-table">
              <thead>
                <tr>
                  <th style="width: 35%">Item</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                ${headerItems.map(item => `
                  <tr>
                    <td class="item-name">
                      ${item.isStarred ? `<span style="color: #f59e0b; margin-right: 4px; font-size: 10pt;">&#9733;</span>` : ""}${item.item}
                    </td>
                    <td class="item-notes">${(item.notes || "").replace(/\n/g, "<br/>")}</td>
                  </tr>
                `).join("")}
              </tbody>
            </table>
          </div>
        `;
      }
    });

    if (!contentHtml) {
      contentHtml = `<p style="text-align: center; color: #666; font-style: italic; margin-top: 30px;">No status items recorded for this week.</p>`;
    }

    const capitalizeName = (name) => {
      if (!name) return "";
      return name.split(" ").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
    };
    const finalSubmitterName = capitalizeName(submitterName || currentUser?.displayName || currentUser?.email?.split("@")[0] || "User");
    const reportDateStr = getWeekDisplayRange(selectedWeek);

    const styles = `
      @page {
        size: letter;
        margin: 0.25in;
      }
      body {
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
        color: #333333;
        margin: 0;
        padding: 0;
        background-color: #f8fafc;
        font-size: 8.5pt;
        line-height: 1.35;
      }
      .content-wrapper {
        background-color: #ffffff;
        max-width: 900px;
        margin: 30px auto;
        padding: 40px;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
        border-radius: 8px;
      }
            .header-container {
              display: flex;
              justify-content: space-between;
              align-items: center;
              border-bottom: 2px solid ${primaryColor};
              padding-bottom: 8px;
              margin-bottom: 12px;
            }
            .header-left {
              display: flex;
              align-items: center;
              gap: 12px;
            }
            .header-left h1 {
              margin: 0 0 3px 0;
              font-size: 14pt;
              color: #111111;
              font-weight: 800;
            }
            .header-left p {
              margin: 0;
              color: #666666;
              font-size: 8.5pt;
            }
            .header-right {
              text-align: right;
            }
            .logo-img {
              max-height: 50px;
              max-width: 50px;
              width: 50px;
              height: 50px;
              object-fit: contain;
              flex-shrink: 0;
            }
            .company-name {
              font-weight: 700;
              color: ${primaryColor};
              font-size: 9.5pt;
            }
            .section-container {
              margin-bottom: 12px;
              page-break-inside: avoid;
            }
            .section-header {
              font-size: 9.5pt;
              font-weight: 700;
              color: #ffffff;
              background-color: ${primaryColor} !important;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
              padding: 4px 8px;
              margin: 0 0 4px 0;
              border-radius: 3px;
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }
            .status-table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 4px;
            }
            .status-table th {
              font-size: 8pt;
              text-transform: uppercase;
              color: ${primaryColor};
              border-bottom: 1.5px solid ${primaryColor};
              padding: 3px 6px;
              text-align: left;
              font-weight: 700;
            }
            .status-table td {
              padding: 4px 6px;
              border-bottom: 1px solid #eeeeee;
              vertical-align: top;
            }
            .item-name {
              font-weight: 600;
              color: #222222;
            }
            .item-notes {
              color: #444444;
            }
            .footer-container {
              border-top: 1px solid #e2e8f0;
              padding-top: 6px;
              margin-top: 12px;
              display: flex;
              justify-content: space-between;
              font-size: 7.5pt;
              color: #888888;
            }
      @media print {
        body {
          background-color: #ffffff;
          padding: 0;
        }
        .content-wrapper {
          box-shadow: none;
          margin: 0;
          padding: 10px;
          border-radius: 0;
          max-width: 100%;
        }
      }
    `;

    const bodyHtml = `
      <div class="content-wrapper">
        <div class="header-container">
            <div class="header-left">
              ${activeLogoUrl ? `<img src="${activeLogoUrl}" class="logo-img" alt="Logo" />` : ""}
              <div>
                <h1>Weekly Status Report</h1>
                <p>Submitter: <strong>${finalSubmitterName}</strong></p>
                <p>Period: <strong>${reportDateStr}</strong></p>
              </div>
            </div>
            <div class="header-right">
              <div class="company-name">${companyName}</div>
            </div>
          </div>

          <div style="margin-bottom: 25px;">
            ${contentHtml}
          </div>

          <div class="footer-container">
            <span>Generated on ${new Date().toLocaleDateString()}</span>
            <span>${companyName} — Confidential</span>
            <span>Page 1 of 1</span>
          </div>
        </div>
      </div>
    `;

    openDocumentPrintWindow({
      title: `Weekly Status Report - ${reportDateStr}`,
      headStyles: styles,
      bodyHtml
    });
  };

  // --- Inline Header States ---
  const [newHeaderName, setNewHeaderName] = useState("");
  const [editingHeaderId, setEditingHeaderId] = useState(null);
  const [editHeaderName, setEditHeaderName] = useState("");

  // --- Inline Weekly Item States ---
  // We keep a dictionary of new item inputs per header to allow concurrent entry
  const [newItemsInput, setNewItemsInput] = useState({});
  const [editingItemId, setEditingItemId] = useState(null);
  const [editItemText, setEditItemText] = useState("");
  const [editItemNotes, setEditItemNotes] = useState("");

  // Adjust selected week by offset in weeks
  const handleShiftWeek = (offsetWeeks) => {
    const date = new Date(selectedWeek + "T00:00:00");
    date.setDate(date.getDate() + offsetWeeks * 7);
    setSelectedWeek(date.toISOString().split("T")[0]);
  };

  const handleDatePickerChange = (e) => {
    if (e.target.value) {
      setSelectedWeek(getMondayOfDate(e.target.value));
    }
  };

  // --- Header Operations ---
  const handleAddHeaderSubmit = async (e) => {
    e.preventDefault();
    if (!newHeaderName.trim()) return;
    try {
      await addHeader(newHeaderName.trim());
      setNewHeaderName("");
    } catch (err) {
      console.error(err);
    }
  };

  const handleStartEditHeader = (header) => {
    setEditingHeaderId(header.id);
    setEditHeaderName(header.name);
  };

  const handleSaveHeader = async (id) => {
    if (!editHeaderName.trim()) return;
    try {
      await updateHeader(id, { name: editHeaderName.trim() });
      setEditingHeaderId(null);
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteHeaderConfirm = async (header) => {
    if (window.confirm(`Are you sure you want to delete the section "${header.name}"? This will also delete all status items inside it.`)) {
      try {
        await deleteHeader(header.id);
      } catch (err) {
        console.error(err);
      }
    }
  };

  // --- Item Operations ---
  const handleNewItemFieldChange = (headerId, field, value) => {
    setNewItemsInput(prev => ({
      ...prev,
      [headerId]: {
        ...(prev[headerId] || { item: "", notes: "" }),
        [field]: value
      }
    }));
  };

  const handleAddItemInline = async (headerId) => {
    const input = newItemsInput[headerId] || { item: "", notes: "" };
    if (!input.item.trim()) return;

    try {
      await addWeeklyItem({
        headerId,
        item: input.item.trim(),
        notes: input.notes.trim()
      });
      // Clear inputs for this header
      setNewItemsInput(prev => ({
        ...prev,
        [headerId]: { item: "", notes: "" }
      }));
    } catch (err) {
      console.error(err);
    }
  };

  const handleStartEditItem = (itemObj) => {
    setEditingItemId(itemObj.id);
    setEditItemText(itemObj.item);
    setEditItemNotes(itemObj.notes || "");
  };

  const handleSaveItemInline = async (id) => {
    if (!editItemText.trim()) return;
    try {
      await updateWeeklyItem(id, {
        item: editItemText.trim(),
        notes: editItemNotes.trim()
      });
      setEditingItemId(null);
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteItem = async (id) => {
    if (window.confirm("Are you sure you want to delete this status line item?")) {
      try {
        await deleteWeeklyItem(id);
      } catch (err) {
        console.error(err);
      }
    }
  };

  return (
    <div className="animate-fade-in" style={{ padding: "1.5rem" }}>
      {/* Page Header */}
      <div className="page-header" style={{ marginBottom: "1.5rem" }}>
        <div className="page-header-info">
          <h1>Weekly Status Updates</h1>
          <p className="text-secondary">Track, update, and organize your weekly Business Development accomplishments.</p>
        </div>
      </div>

      {/* Tabs Menu */}
      <div className="settings-tabs-container" style={{ marginBottom: "1.5rem", borderBottom: "1px solid var(--border-color)", display: "flex", gap: "1rem" }}>
        <button
          className={`settings-tab-btn ${activeTab === "status" ? "active" : ""}`}
          onClick={() => setActiveTab("status")}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            padding: "0.75rem 1rem",
            background: "none",
            border: "none",
            borderBottom: activeTab === "status" ? "2px solid var(--primary)" : "2px solid transparent",
            color: activeTab === "status" ? "var(--text-primary)" : "var(--text-muted)",
            fontWeight: "600",
            cursor: "pointer"
          }}
        >
          <ClipboardCheck size={18} />
          Weekly Status
        </button>
        <button
          className={`settings-tab-btn ${activeTab === "headers" ? "active" : ""}`}
          onClick={() => setActiveTab("headers")}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            padding: "0.75rem 1rem",
            background: "none",
            border: "none",
            borderBottom: activeTab === "headers" ? "2px solid var(--primary)" : "2px solid transparent",
            color: activeTab === "headers" ? "var(--text-primary)" : "var(--text-muted)",
            fontWeight: "600",
            cursor: "pointer"
          }}
        >
          <LayoutList size={18} />
          Headers / Sections
        </button>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="login-error-alert animate-fade-in" style={{ marginBottom: "1.5rem" }}>
          {error}
        </div>
      )}

      {/* Main Tab Content */}
      {activeTab === "status" && (
        <div>
          {/* Week Date Picker Navigation Bar */}
          <div className="table-actions-container" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", backgroundColor: "var(--bg-card)", padding: "1rem", borderRadius: "8px", border: "1px solid var(--border-color)", marginBottom: "1.5rem" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
              <button className="btn btn-secondary btn-icon" onClick={() => handleShiftWeek(-1)} title="Previous Week">
                <ChevronLeft size={18} />
              </button>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <span className="text-secondary" style={{ fontSize: "0.8rem", fontWeight: "600", textTransform: "uppercase" }}>Week Start (Monday)</span>
                <span style={{ fontWeight: "700", color: "var(--text-primary)", fontSize: "1.1rem" }}>{getWeekDisplayRange(selectedWeek)}</span>
              </div>
              <button className="btn btn-secondary btn-icon" onClick={() => handleShiftWeek(1)} title="Next Week">
                <ChevronRight size={18} />
              </button>
            </div>
            
            <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <Calendar size={18} className="text-secondary" />
                <input
                  type="date"
                  className="form-input"
                  value={selectedWeek}
                  onChange={handleDatePickerChange}
                  style={{ width: "160px", height: "38px", margin: 0 }}
                />
              </div>
              <button 
                className="btn btn-primary" 
                onClick={handleExportPDF}
                style={{ margin: 0, display: "flex", alignItems: "center", gap: "0.5rem", height: "38px" }}
                disabled={headers.length === 0}
                title="Export report to print/save as PDF"
              >
                <Printer size={16} />
                Export Report
              </button>
            </div>
          </div>

          {loading ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "4rem 0", gap: "1rem", color: "var(--text-secondary)" }}>
              <Loader2 className="spinner-icon" size={36} />
              <span>Loading Weekly Status...</span>
            </div>
          ) : headers.length === 0 ? (
            <div style={{ textAlign: "center", padding: "4rem 2rem", backgroundColor: "var(--bg-card)", border: "1px dashed var(--border-color)", borderRadius: "8px" }}>
              <h3>No Sections Defined</h3>
              <p style={{ color: "var(--text-secondary)", marginBottom: "1rem" }}>Define the sections (Headers) of your weekly status sheet before adding line items.</p>
              <button className="btn btn-primary" onClick={() => setActiveTab("headers")}>
                Go to Headers Tab
              </button>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "2rem" }}>
              {headers.map(header => {
                const headerItems = items.filter(item => item.headerId === header.id);
                const headerInput = newItemsInput[header.id] || { item: "", notes: "" };

                return (
                  <div key={header.id} className="proc-card" style={{ border: "1px solid var(--border-color)", borderRadius: "8px", overflow: "hidden" }}>
                    {/* Header Title */}
                    <div style={{ backgroundColor: "var(--bg-sidebar)", padding: "0.75rem 1rem", borderBottom: "1px solid var(--border-color)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <h3 style={{ margin: 0, fontSize: "1rem", fontWeight: "700", color: "var(--primary)" }}>{header.name}</h3>
                      <span className="sku-badge" style={{ fontSize: "0.75rem" }}>{headerItems.length} Item{headerItems.length !== 1 ? "s" : ""}</span>
                    </div>

                    <div style={{ padding: "1rem", backgroundColor: "var(--bg-card)" }}>
                      {/* Items Table */}
                      <table className="proc-table" style={{ width: "100%", marginBottom: "1.5rem" }}>
                        <thead>
                          <tr>
                            <th style={{ width: "45px" }} className="proc-th-center" title="Star item to automatically roll over to next week">Star</th>
                            <th style={{ width: "35%" }}>Item (Task Completed)</th>
                            <th>Notes (Paragraph Details)</th>
                            <th style={{ width: "100px" }} className="proc-th-center">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {headerItems.length === 0 ? (
                            <tr>
                              <td colSpan="4" className="text-center" style={{ color: "var(--text-muted)", fontStyle: "italic", padding: "1.5rem 0" }}>
                                No items recorded under this section for this week.
                              </td>
                            </tr>
                          ) : (
                            headerItems.map(item => {
                              const isEditing = editingItemId === item.id;
                              return (
                                <tr key={item.id}>
                                  <td className="proc-td-center">
                                    <button
                                      type="button"
                                      onClick={() => toggleStar(item)}
                                      style={{
                                        background: "none",
                                        border: "none",
                                        cursor: "pointer",
                                        padding: "4px",
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: item.isStarred ? "#f59e0b" : "var(--text-muted)",
                                        transition: "all 0.15s ease"
                                      }}
                                      title={item.isStarred ? "Starred — auto-forwarded to next week's status (Click to unstar)" : "Star this item to automatically roll over to next week's status"}
                                    >
                                      <Star 
                                        size={18} 
                                        fill={item.isStarred ? "#f59e0b" : "none"} 
                                        stroke={item.isStarred ? "#f59e0b" : "currentColor"} 
                                      />
                                    </button>
                                  </td>
                                  <td>
                                    {isEditing ? (
                                      <input
                                        type="text"
                                        className="form-input"
                                        value={editItemText}
                                        onChange={(e) => setEditItemText(e.target.value)}
                                        style={{ width: "100%", margin: 0 }}
                                        placeholder="Completed task name..."
                                      />
                                    ) : (
                                      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
                                        <span style={{ fontWeight: "600", color: "var(--text-primary)" }}>{item.item}</span>
                                        {item.sourceItemId && (
                                          <span 
                                            style={{ 
                                              fontSize: "0.7rem", 
                                              padding: "0.15rem 0.4rem", 
                                              backgroundColor: "rgba(245, 158, 11, 0.12)", 
                                              color: "#f59e0b", 
                                              border: "1px solid rgba(245, 158, 11, 0.3)", 
                                              borderRadius: "4px",
                                              fontWeight: "600",
                                              display: "inline-flex",
                                              alignItems: "center",
                                              gap: "0.25rem"
                                            }}
                                            title="Carried over from prior week's starred status"
                                          >
                                            <Star size={10} fill="#f59e0b" stroke="#f59e0b" /> Rolled Over
                                          </span>
                                        )}
                                      </div>
                                    )}
                                  </td>
                                  <td>
                                    {isEditing ? (
                                      <textarea
                                        className="form-input"
                                        value={editItemNotes}
                                        onChange={(e) => setEditItemNotes(e.target.value)}
                                        style={{ width: "100%", margin: 0, minHeight: "60px", resize: "vertical" }}
                                        placeholder="Details/notes..."
                                      />
                                    ) : (
                                      <span style={{ whiteSpace: "pre-wrap", color: "var(--text-secondary)" }}>
                                        {item.notes || <span className="text-muted" style={{ fontStyle: "italic" }}>No notes added</span>}
                                      </span>
                                    )}
                                  </td>
                                  <td className="proc-td-center">
                                    {isEditing ? (
                                      <div style={{ display: "flex", gap: "0.25rem", justifyContent: "center" }}>
                                        <button
                                          className="btn btn-secondary btn-icon"
                                          onClick={() => handleSaveItemInline(item.id)}
                                          title="Save Line Item"
                                          style={{ padding: "0.25rem", minHeight: "auto", color: "var(--success)" }}
                                        >
                                          <Check size={16} />
                                        </button>
                                        <button
                                          className="btn btn-secondary btn-icon"
                                          onClick={() => setEditingItemId(null)}
                                          title="Cancel Edit"
                                          style={{ padding: "0.25rem", minHeight: "auto", color: "var(--danger)" }}
                                        >
                                          <X size={16} />
                                        </button>
                                      </div>
                                    ) : (
                                      <div style={{ display: "flex", gap: "0.25rem", justifyContent: "center" }}>
                                        <button
                                          className="btn btn-secondary btn-icon"
                                          onClick={() => handleStartEditItem(item)}
                                          title="Edit Line Item"
                                          style={{ padding: "0.25rem", minHeight: "auto" }}
                                        >
                                          <Edit2 size={14} />
                                        </button>
                                        <button
                                          className="btn btn-secondary btn-icon"
                                          onClick={() => handleDeleteItem(item.id)}
                                          title="Delete Line Item"
                                          style={{ padding: "0.25rem", minHeight: "auto", color: "var(--danger)" }}
                                        >
                                          <Trash2 size={14} />
                                        </button>
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              );
                            })
                          )}

                          {/* Inline Add Item Row */}
                          <tr style={{ backgroundColor: "rgba(255,255,255,0.01)" }}>
                            <td className="proc-td-center">
                              <span style={{ color: "var(--text-muted)", opacity: 0.4 }}>—</span>
                            </td>
                            <td>
                              <input
                                type="text"
                                className="form-input"
                                placeholder="New completed task name..."
                                value={headerInput.item || ""}
                                onChange={(e) => handleNewItemFieldChange(header.id, "item", e.target.value)}
                                style={{ width: "100%", margin: 0 }}
                              />
                            </td>
                            <td>
                              <textarea
                                className="form-input"
                                placeholder="Task details/notes..."
                                value={headerInput.notes || ""}
                                onChange={(e) => handleNewItemFieldChange(header.id, "notes", e.target.value)}
                                style={{ width: "100%", margin: 0, minHeight: "38px", height: "38px", resize: "vertical" }}
                              />
                            </td>
                            <td className="proc-td-center">
                              <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() => handleAddItemInline(header.id)}
                                style={{ padding: "0.4rem 0.8rem", minHeight: "auto", fontSize: "0.85rem", margin: 0, width: "100%" }}
                                disabled={!headerInput.item || !headerInput.item.trim()}
                              >
                                <Plus size={14} />
                                Add
                              </button>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Headers / Sections Tab Content */}
      {activeTab === "headers" && (
        <div className="proc-card" style={{ border: "1px solid var(--border-color)", padding: "1.5rem", borderRadius: "8px", backgroundColor: "var(--bg-card)", maxWidth: "800px" }}>
          <h2 style={{ fontSize: "1.2rem", fontWeight: "700", marginBottom: "1rem" }}>Manage Report Sections (Headers)</h2>
          <p className="text-secondary" style={{ marginBottom: "1.5rem" }}>Create, rename, or delete headers. These headers define the sections of your weekly accomplishments status sheet.</p>

          {/* List of Headers */}
          <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", marginBottom: "2rem" }}>
            {headers.length === 0 ? (
              <div style={{ color: "var(--text-muted)", fontStyle: "italic", padding: "1rem 0" }}>
                No sections defined. Create a new section below.
              </div>
            ) : (
              headers.map(header => {
                const isEditing = editingHeaderId === header.id;

                return (
                  <div
                    key={header.id}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "0.75rem 1rem",
                      backgroundColor: "rgba(255,255,255,0.01)",
                      border: "1px solid var(--border-color)",
                      borderRadius: "6px"
                    }}
                  >
                    {isEditing ? (
                      <input
                        type="text"
                        className="form-input"
                        value={editHeaderName}
                        onChange={(e) => setEditHeaderName(e.target.value)}
                        style={{ width: "350px", margin: 0 }}
                        placeholder="Header name..."
                      />
                    ) : (
                      <span style={{ fontWeight: "700", color: "var(--text-primary)" }}>{header.name}</span>
                    )}

                    <div style={{ display: "flex", gap: "0.5rem" }}>
                      {isEditing ? (
                        <>
                          <button
                            className="btn btn-secondary btn-icon"
                            onClick={() => handleSaveHeader(header.id)}
                            style={{ color: "var(--success)" }}
                            title="Save Name"
                          >
                            <Check size={16} />
                          </button>
                          <button
                            className="btn btn-secondary btn-icon"
                            onClick={() => setEditingHeaderId(null)}
                            style={{ color: "var(--danger)" }}
                            title="Cancel Edit"
                          >
                            <X size={16} />
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            className="btn btn-secondary btn-icon"
                            onClick={() => handleStartEditHeader(header)}
                            title="Rename Section"
                          >
                            <Edit2 size={14} />
                          </button>
                          <button
                            className="btn btn-secondary btn-icon"
                            onClick={() => handleDeleteHeaderConfirm(header)}
                            style={{ color: "var(--danger)" }}
                            title="Delete Section"
                          >
                            <Trash2 size={14} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Add Header Inline Form */}
          <form onSubmit={handleAddHeaderSubmit} style={{ borderTop: "1px solid var(--border-color)", paddingTop: "1.5rem" }}>
            <h3 style={{ fontSize: "1rem", fontWeight: "700", marginBottom: "0.75rem" }}>Add New Section Header</h3>
            <div style={{ display: "flex", gap: "0.75rem" }}>
              <input
                type="text"
                className="form-input"
                placeholder="e.g. Completed Tasks, In-Progress, Blockers..."
                value={newHeaderName}
                onChange={(e) => setNewHeaderName(e.target.value)}
                style={{ flex: 1, margin: 0 }}
              />
              <button
                type="submit"
                className="btn btn-primary"
                disabled={!newHeaderName.trim()}
                style={{ margin: 0, display: "flex", alignItems: "center", gap: "0.5rem" }}
              >
                <Plus size={16} />
                Create Section
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

// Inline Loader component helper for local spinner
function Loader2({ className, size }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size || 24}
      height={size || 24}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  );
}
